/**
 * features/auth/LoginPage.tsx
 */

import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { ApiError } from '~/lib/api';
import { Alert, Button, Field, Input } from '~/ui/primitives';
import { useAuth } from './auth-context';

export function LoginPage() {
  const { user, login, initializing } = useAuth();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ล็อกอินอยู่แล้ว — กลับไปหน้าที่พยายามเข้าก่อนหน้านี้
  if (!initializing && user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await login(username.trim(), password);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ กรุณาลองใหม่',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-7 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">FT-Account</h1>
          <p className="mt-1.5 text-sm text-fg-muted">ระบบบัญชีสำหรับธุรกิจ SME</p>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-4 p-5" noValidate>
          {error ? <Alert tone="danger">{error}</Alert> : null}

          <Field label="ชื่อผู้ใช้" htmlFor="username" required>
            <Input
              id="username"
              name="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              autoFocus
              required
              invalid={Boolean(error)}
            />
          </Field>

          <Field label="รหัสผ่าน" htmlFor="password" required>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                invalid={Boolean(error)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((current) => !current)}
                className="absolute top-0 right-0 flex h-9 w-9 items-center justify-center text-fg-subtle hover:text-fg"
                aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </Field>

          <Button type="submit" variant="primary" loading={submitting} className="w-full">
            เข้าสู่ระบบ
          </Button>
        </form>

        <p className="mt-5 text-center text-xs text-fg-subtle">
          Forktech Group · ระบบภายในองค์กร
        </p>
      </div>
    </div>
  );
}
